import React, { startTransition, useState } from 'react';

import { Button, IconPlay, ImgLazyLoader, Spinner, SpinnerInline } from '../index';

/*
 *  Youtube Lazy Loader Component
 *
 *  Accepts iframe properties and loads placeholder photo lazily from youtube based on
 *    video id in props.src
 *
 *  Uses 2 state variables to conditionally render either a placeholder image
 *  or a youtube embed iframe.
 *
 *  Default state: load = false, loading = false; Displays placeholder, play button active
 *  When user clicks within placeholder load is set to true and iframe is loaded with display: none;
 *    to allow iframe to load "in background", play button disabled
 *  Once iframe load event is recieved loading is set to true, placeholder is removed and iframe is set to display: block;
 */

export type YoutubeLazyLoaderProps = React.PropsWithChildren<
React.IframeHTMLAttributes<{
  allowfullscreen: boolean,
  frameborder: boolean,
}>
& {
  hidePlayButton?: boolean,
}
>;

export const YoutubeLazyLoader = (props : YoutubeLazyLoaderProps) => {
  const [loaded, setLoaded] = useState(false);
  const [load, setLoad] = useState(false);

  const videoIdmatch = props.src?.match(/https:\/\/www.youtube.com\/embed\/(.+)\?/);
  const videoId = videoIdmatch && videoIdmatch[1] || -1;

  /* replace html attributes with attributes react won't complain about */
  const swappedAttribs = Object.assign({}, props) as any;

  swappedAttribs.src += '&autoplay=true&showinfo=0&autohide=1';
  swappedAttribs.allowFullScreen = swappedAttribs.allowfullscreen;
  swappedAttribs.frameBorder = swappedAttribs.frameborder;
  delete swappedAttribs.allowfullscreen;
  delete swappedAttribs.frameborder;

  return (
    <>
      {!loaded
        && <Button unstyled
          className='relative aspect-video max-w-full overflow-hidden'
          onPress = {
            () => {
              /* Only respond to click once.  Disabled afterwards */
              if(!load)
              {
                setLoad(true);
              }
            }
          }
        >
          <ImgLazyLoader>
            <object data={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} type='image/jpeg' className='w-full'>
              <object data={`https://img.youtube.com/vi/${videoId}/sddefault.jpg`} type='image/jpeg'>
                <object data={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} type='image/jpeg'/>
              </object>
            </object>
          </ImgLazyLoader>
          {!props.hidePlayButton && (
            <div className='absolute left-0 right-0 top-0 bottom-0 flex items-center justify-center'>
              {load ? <SpinnerInline className='w-full max-w-32 h-2'/> : <Button unstyled className='text-white' divButton>
                <IconPlay className='h-16 w-16'/>
              </Button>}
            </div>
          )}
        </Button>
      }

      {load
        && <iframe
          { ...swappedAttribs }
          className='aspect-video'
          style = {
            {
              display: loaded ? 'block' : 'none',
            }
          }
          onLoad = { () => {
            setTimeout(() => startTransition(() => setLoaded(true)), 150);
          }}
        />
      }
    </>
  );
};
